/**
 * Section Title Peel Effect
 * Handles scroll detection and animation triggers for the black peel-off effect
 */

// Prevent duplicate class declarations
if (typeof SectionTitlePeelEffect === 'undefined') {

class SectionTitlePeelEffect {
  constructor() {
    this.titleElements = [];
    this.observerOptions = {
      root: null,
      rootMargin: '-10% 0px -10% 0px',
      threshold: [0, 0.1, 0.5]
    };
    this.observer = null;
    this.animatedElements = new Set();
    
    this.init();
  }

  init() {
    // Wait for DOM content to be loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.findTitleElements();
    this.setupIntersectionObserver();
    this.startObserving();
    
    // Listen for theme section reloads (Shopify customizer)
    document.addEventListener('shopify:section:load', (event) => {
      this.handleSectionReload(event.detail.sectionId);
    });
  }

  findTitleElements() {
    // Find all elements with the peel effect class
    const containers = document.querySelectorAll('.title-peel-container');
    
    this.titleElements = Array.from(containers).filter(container => {
      // Ensure the container has the required child elements
      const overlay = container.querySelector('.title-peel-overlay');
      const dangle = container.querySelector('.title-peel-dangle');
      const text = container.querySelector('.title-peel-text');
      
      return overlay && dangle && text;
    });

    // Assign random peel direction and curve variation to each element
    this.titleElements.forEach(element => {
      if (!element.classList.contains('peel-direction-set')) {
        const directions = ['peel-left', 'peel-right'];
        const randomDirection = directions[Math.floor(Math.random() * directions.length)];
        element.classList.add(randomDirection);
        
        // Add curve variation for more organic look
        if (Math.random() > 0.5) {
          element.classList.add('peel-curve');
        }
        
        element.classList.add('peel-direction-set');
      }
    });

    console.log(`Found ${this.titleElements.length} title elements with peel effect`);
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const element = entry.target;
        
        if (entry.isIntersecting && !this.animatedElements.has(element)) {
          this.triggerPeelAnimation(element);
        }
      });
    }, this.observerOptions);
  }

  startObserving() {
    this.titleElements.forEach(element => {
      this.observer.observe(element);
    });
  }

  triggerPeelAnimation(element) {
    // Check if element is still in the DOM
    if (!document.body.contains(element)) {
      return;
    }

    // Mark as animated to prevent retriggering
    this.animatedElements.add(element);
    
    // Add a small random delay for more natural feel
    const randomDelay = Math.random() * 200;
    
    setTimeout(() => {
      element.classList.add('peel-animated');
      
      // Emit custom event for other scripts to listen to
      const peelEvent = new CustomEvent('sectionTitlePeelStart', {
        detail: {
          element: element,
          direction: element.classList.contains('peel-left') ? 'left' : 'right',
          hasCurve: element.classList.contains('peel-curve')
        }
      });
      element.dispatchEvent(peelEvent);
      
    }, randomDelay);
  }

  handleSectionReload(sectionId) {
    // When a section is reloaded in the customizer, re-scan for new elements
    setTimeout(() => {
      this.findTitleElements();
      this.startObserving();
    }, 100);
  }

  // Public method to manually trigger animation
  triggerAnimation(selector) {
    const element = document.querySelector(selector);
    if (element && this.titleElements.includes(element)) {
      this.triggerPeelAnimation(element);
    }
  }

  // Public method to reset an animation
  resetAnimation(selector) {
    const element = document.querySelector(selector);
    if (element) {
      element.classList.remove('peel-animated');
      this.animatedElements.delete(element);
    }
  }

  // Cleanup method
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.titleElements = [];
    this.animatedElements.clear();
  }
}

// Utility function to create the HTML structure for the peel effect
function createPeelEffectHTML(titleText, additionalClasses = '') {
  return `
    <div class="title-peel-container ${additionalClasses}">
      <div class="title-peel-text">${titleText}</div>
      <div class="title-peel-overlay" aria-hidden="true"></div>
      <div class="title-peel-dangle" aria-hidden="true"></div>
    </div>
  `;
}

// Helper function to wrap existing titles with peel effect
function wrapTitleWithPeelEffect(titleElement, additionalClasses = '') {
  if (!titleElement || titleElement.closest('.title-peel-container')) {
    return; // Already wrapped or element doesn't exist
  }

  const originalHTML = titleElement.innerHTML;
  const originalClasses = titleElement.className;
  
  // Create the peel effect container
  const container = document.createElement('div');
  container.className = `title-peel-container ${additionalClasses}`;
  
  // Create the text element with original classes
  const textElement = document.createElement('div');
  textElement.className = `title-peel-text ${originalClasses}`;
  textElement.innerHTML = originalHTML;
  
  // Create overlay and dangle elements
  const overlay = document.createElement('div');
  overlay.className = 'title-peel-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  
  const dangle = document.createElement('div');
  dangle.className = 'title-peel-dangle';
  dangle.setAttribute('aria-hidden', 'true');
  
  // Assemble the structure
  container.appendChild(textElement);
  container.appendChild(overlay);
  container.appendChild(dangle);
  
  // Replace the original element
  titleElement.parentNode.replaceChild(container, titleElement);
}

// Auto-initialize if DOM is ready (only if not already initialized)
if (!window.titlePeelEffect) {
  let titlePeelEffect;
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      titlePeelEffect = new SectionTitlePeelEffect();
      window.titlePeelEffect = titlePeelEffect;
    });
  } else {
    titlePeelEffect = new SectionTitlePeelEffect();
    window.titlePeelEffect = titlePeelEffect;
  }
}

// Export for use in other scripts (only if not already exported)
if (!window.SectionTitlePeelEffect) {
  window.SectionTitlePeelEffect = SectionTitlePeelEffect;
  window.createPeelEffectHTML = createPeelEffectHTML;
  window.wrapTitleWithPeelEffect = wrapTitleWithPeelEffect;
}

// Handle page navigation (for SPAs or theme switching)
window.addEventListener('beforeunload', () => {
  if (window.titlePeelEffect) {
    window.titlePeelEffect.destroy();
  }
});

// Performance monitoring
if (window.PerformanceObserver) {
  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    entries.forEach(entry => {
      if (entry.name.includes('peel-effect') && entry.duration > 16) {
        console.warn(`Peel effect animation took ${entry.duration}ms - consider optimizing`);
      }
    });
  });
  
  try {
    observer.observe({ entryTypes: ['measure'] });
  } catch (e) {
    // Performance observer not supported
  }
}

} // End of SectionTitlePeelEffect undefined check